import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import IconClose from '../../../icons/icon-close.svg';
import { readPreference, writePreference } from '../../../functions/popover';
import { fitFrame, fitSize, moveFrame, placeBeside, presetFrame, resizeFrame } from '../../../functions/windowFrame';

import './styles.scss';

// The element windows are drawn in: the day's modal, or the page's layer for windows outside it.
const LayerContext = createContext(null);
export const WindowLayer = LayerContext.Provider;

// The window a component is in: its frame and a way to move it sideways. None on the page itself.
const WindowContext = createContext(null);

const EDGES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
// A press on these moves nothing, whatever the window's own cancel says.
const INTERACTIVE = 'input, textarea, select, button, a, label, [contenteditable="true"]';
// How far the pointer goes before a press becomes a move, so that clicks stay clicks.
const MOVE_THRESHOLD = 3;

// The window pressed last is drawn over the others.
let topZ = 0;

const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });
const frameOf = (element) => {
    if (!element) return null;
    const { x, y, width, height } = element.getBoundingClientRect();
    return { x, y, width, height };
};

// Follows a press with the pointer until it's let go, with the text under it left unselected.
const followPointer = (event, onMove) => {
    const startX = event.clientX;
    const startY = event.clientY;
    const userSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const move = (e) => onMove(e.clientX - startX, e.clientY - startY);
    const stop = () => {
        document.body.style.userSelect = userSelect;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
};

// A window over the page, moved by pressing anywhere in it but on cancel's elements and the ones that take
// input, and resized from its edges unless resizable is false, when it takes its content's size. Opened from
// another window, it opens beside that one, which may move aside for it; else beside the element in besideRef,
// or in the middle. It remembers its size and preset in this browser under name. A resizable one has a bar with
// its controls across its top, with a close button for onClose, titled closeTitle.
function FloatingWindow({ name, defaultSize, minSize, cancel, resizable = true, besideRef, onClose, closeTitle, className, children }) {
    const layer = useContext(LayerContext) ?? document.body;
    const opener = useContext(WindowContext);
    const ref = useRef(null);
    const storageKey = `window.${name}`;
    // Where a window of size opens, and where its opener moves to make room, if it has to.
    const openingPlace = (size) => placeBeside(opener?.frame ?? frameOf(besideRef?.current), size, viewport());
    // The frame; preset, 'height' or 'full', with restore, the frame to go back to from it. And where the opener
    // moves to, if it has to.
    const [opening] = useState(() => {
        if (!resizable) return { state: { frame: null, preset: null, restore: null } };
        const saved = readPreference(storageKey, null);
        const size = fitSize(saved?.size ?? defaultSize, minSize, viewport());
        const { anchorX, ...place } = openingPlace(size);
        const frame = { ...place, ...size };
        const preset = saved?.preset ?? null;
        return preset
            ? { state: { frame: presetFrame(preset, frame, viewport()), preset, restore: frame } }
            : { state: { frame, preset: null, restore: null }, openerX: opener ? anchorX : undefined };
    });
    const [state, setState] = useState(opening.state);
    const [z, setZ] = useState(() => ++topZ);
    const { frame, preset, restore } = state;

    // Once, as it opens: after that the opener goes where it's moved.
    const openerMoved = useRef(false);
    useLayoutEffect(() => {
        if (openerMoved.current || opening.openerX == null || !opener) return;
        openerMoved.current = true;
        opener.moveTo(opening.openerX);
    }, [opening, opener]);

    // A window of its content's size opens once that's known.
    useLayoutEffect(() => {
        if (frame) return;
        const size = { width: ref.current.offsetWidth, height: ref.current.offsetHeight };
        setState((s) => ({ ...s, frame: { ...placeBeside(null, size, viewport()), ...size } }));
    }, [frame]);

    useEffect(() => {
        if (!resizable) return undefined;
        const fit = () => setState((s) => ({
            ...s, frame: s.preset ? presetFrame(s.preset, s.frame, viewport()) : fitFrame(s.frame, viewport()),
        }));
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [resizable]);

    // The size it comes back to, outside a preset.
    const savedWidth = resizable && frame ? (restore ?? frame).width : null;
    const savedHeight = resizable && frame ? (restore ?? frame).height : null;
    useEffect(() => {
        if (savedWidth == null) return;
        writePreference(storageKey, { size: { width: savedWidth, height: savedHeight }, preset });
    }, [storageKey, savedWidth, savedHeight, preset]);

    const moveTo = (x) => setState((s) => ({ ...s, frame: { ...s.frame, x } }));

    const togglePreset = (next) => setState((s) => {
        if (s.preset === next) return { frame: fitFrame(s.restore, viewport()), preset: null, restore: null };
        const base = s.restore ?? s.frame;
        return { frame: presetFrame(next, base, viewport()), preset: next, restore: base };
    });

    // Back to the size it first opened at and to where it opens, its own size forgotten.
    const reset = () => {
        const size = fitSize(defaultSize, minSize, viewport());
        const { anchorX, ...place } = openingPlace(size);
        if (anchorX != null) opener?.moveTo(anchorX);
        setState({ frame: { ...place, ...size }, preset: null, restore: null });
    };

    // Presses in the windows opened from this one reach it too, through React, though they're elsewhere.
    const isOwn = (e) => ref.current.contains(e.target);

    const startMove = (e) => {
        if (e.button !== 0 || !frame || preset === 'full' || !isOwn(e)) return;
        if (e.target.closest(INTERACTIVE) || (cancel && e.target.closest(cancel))) return;
        const start = frame;
        let moving = false;
        followPointer(e, (dx, dy) => {
            if (!moving) moving = Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD;
            if (!moving) return;
            // A window of full height moves only sideways and stays so.
            const next = moveFrame(start, dx, preset === 'height' ? 0 : dy, viewport());
            setState((s) => ({ ...s, frame: next }));
        });
    };

    const startResize = (edge) => (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const start = frame;
        followPointer(e, (dx, dy) => setState({
            frame: resizeFrame(start, edge, dx, dy, minSize, viewport()), preset: null, restore: null,
        }));
    };

    const style = frame
        ? { left: frame.x, top: frame.y, width: resizable ? frame.width : undefined, height: resizable ? frame.height : undefined, zIndex: z }
        : { visibility: 'hidden', zIndex: z };

    return createPortal(
        <WindowContext.Provider value={{ frame, moveTo }}>
            <div
                ref={ref}
                className={classNames('floating-window', className, { 'floating-window_resizable': resizable })}
                style={style}
                onPointerDownCapture={(e) => isOwn(e) && setZ(++topZ)}
                onPointerDown={startMove}
            >
                {resizable ? (
                    <>
                        <div className="floating-window__bar">
                            <WindowControls preset={preset} togglePreset={togglePreset} reset={reset} onClose={onClose} closeTitle={closeTitle} />
                        </div>
                        <div className="floating-window__content">{children}</div>
                        {EDGES.map((edge) => (
                            <div key={edge} className={`floating-window__edge floating-window__edge_${edge}`} onPointerDown={startResize(edge)} />
                        ))}
                    </>
                ) : children}
            </div>
        </WindowContext.Provider>,
        layer,
    );
}

const icon = (paths) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        {paths}
    </svg>
);
const RESET_ICON = icon(<><path d="M3 12a9 9 0 1 0 2.64-6.36L3 8.3" /><path d="M3 3v5.3h5.3" /></>);
const HEIGHT_ICON = icon(<><rect x="6" y="3" width="12" height="18" rx="1.5" /><path d="M12 7v10M9.5 9.5 12 7l2.5 2.5M9.5 14.5 12 17l2.5-2.5" /></>);
const FULL_ICON = icon(<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />);

// The window's controls: back to the size it opened at; full height, and full height and width, each pressed
// again giving back the size from before; and closing it, if it can be closed.
function WindowControls({ preset, togglePreset, reset, onClose, closeTitle }) {
    const presetButton = (name, title, icon) => (
        <button
            type="button"
            title={preset === name ? 'Вернуть размер' : title}
            className={classNames('window-control', { 'window-control_active': preset === name })}
            onClick={() => togglePreset(name)}
        >
            {icon}
        </button>
    );

    return (
        <span className="window-controls">
            <button type="button" title="Исходный размер" className="window-control" onClick={reset}>{RESET_ICON}</button>
            {presetButton('height', 'Во всю высоту', HEIGHT_ICON)}
            {presetButton('full', 'Во весь экран', FULL_ICON)}
            {onClose && (
                <button type="button" title={closeTitle} className="window-control window-control_close" onClick={onClose}>
                    <img src={IconClose} alt="close" />
                </button>
            )}
        </span>
    );
}

export { FloatingWindow };
